import {hasInjectionContext, inject, unref} from 'vue';
import {useCatalog as useCatalogBase} from '../../nuxt-base/composables/useCatalog.js';
import {requestDeadline, awaitWithSignal} from '../utils/requestDeadline.mjs';

export function useCatalog(opts = {}) {
	const catalog = useCatalogBase(opts);
	const originalFetchProducts = catalog.fetchProducts;
	const catalogCategoryData = hasInjectionContext() ? inject('baseCatalogCategoryData', null) : null;
	const runtimeConfig = useRuntimeConfig();
	const searchApiEnabled = runtimeConfig.public?.searchApiEnabled === true;

	function stripHtml(value) {
		return String(value || '').replace(/<[^>]*>/g, '').trim();
	}

	function getCategorySearchQuery() {
		const category = unref(catalogCategoryData?.category);
		return stripHtml(category?.title || category?.seo_h1 || category?.name);
	}

	function getSearchApiOptions(options = {}) {
		if (!searchApiEnabled) return null;

		if (options?.search_q) return {...options, query: options.search_q, limit: options?.limit || 30};

		if (unref(catalogCategoryData?.contentType) === 'category') {
			const category = unref(catalogCategoryData?.category);
			const categoryCode = category?.code || options?.category_code || options?.category_codes;
			const query = getCategorySearchQuery();
			if (categoryCode) {
				return {
					...options,
					query: '',
					category_code: categoryCode,
					category_level: options?.category_level || 'category',
					limit: options?.limit || 30,
					sort: options?.sort || 'priority',
					search_source: 'category',
				};
			}
			if (query) return {...options, query, limit: options?.limit || 30, search_source: 'category'};
		}

		return null;
	}

	catalog.fetchProducts = async (options = {}, config = {}) => {
		const searchApiOptions = getSearchApiOptions(options);
		let response;

		if (searchApiOptions) {
			const deadline = requestDeadline(Math.min(Number(runtimeConfig.public?.catalogRequestTimeoutMs) || 40000, Number(config.requestOptions?.timeout) > 0 ? Number(config.requestOptions.timeout) : Infinity), config.requestOptions?.signal);
			try {
				const search = requestDeadline(Number(runtimeConfig.public?.searchApiTimeoutMs) || 30000, deadline.signal);
				try {
					search.signal.throwIfAborted();
					response = await awaitWithSignal($fetch('/_search/products', {
						method: 'POST',
						body: searchApiOptions,
						signal: search.signal, timeout: 0, retry: false,
					}), search.signal);
				} finally {search.close();}
			} catch (error) {
				deadline.signal.throwIfAborted();
				// Deep crawler pagination must never reach the legacy catalog on Search failure.
				const fallbackPage = Math.max(1, Number.parseInt(options.page, 10) || 1, Number.parseInt(options.to_page, 10) || 1);
				if (fallbackPage >= 20) throw error;
				response = await awaitWithSignal(originalFetchProducts(options, {...config, requestOptions: {...config.requestOptions, signal: deadline.signal, timeout: deadline.remaining(), retry: false}}), deadline.signal);
			} finally {deadline.close();}
		} else {
			response = await originalFetchProducts(options, config);
		}

		if (import.meta.client) {
			const {trackFiltersFromProductsResponse} = useEventWeb();
			trackFiltersFromProductsResponse(options, response);
		}

		return response;
	};

	const originalFetchWishlist = catalog.fetchWishlistProducts;
	catalog.fetchWishlistProducts = async (...args) => {
		const nuxtApp = useNuxtApp();
		const started = nuxtApp._bigbangKeycloak?.generation || 0;
		const response = await originalFetchWishlist(...args);
		if (started === (nuxtApp._bigbangKeycloak?.generation || 0)) return response;
		useState('wishlistProducts').value = [];
		return {...response, data: null};
	};

	return catalog;
}
